const API = '/api/contracts';

export interface ContractSummary {
  id: string;
  title: string;
  status_name: string | null;
  status_color: string | null;
  type_doc_name: string | null;
  organization_name: string | null;
  date_accept_iso: string | null;
  ref_doc_id: string | null;
  ref_doc_type: string | null;
}

export interface ContractDetail extends ContractSummary {
  access_group: string | null;
  scraped_at: string | null;
  full_html: string | null;
  full_markdown: string | null;
}

export interface ContractSearchHit {
  id: string;
  title: string;
  status_name: string | null;
  type_doc_name: string | null;
  organization_name: string | null;
  date_accept_iso: string | null;
  ref_doc_id: string | null;
  snippet: string | null;
  rank: number;
}

export interface ContractListParams {
  q?: string;
  status?: string;
  type_doc?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export async function listContracts(params: ContractListParams = {}): Promise<ContractSummary[]> {
  const sp = new URLSearchParams();
  if (params.q) sp.set('q', params.q);
  if (params.status) sp.set('status', params.status);
  if (params.type_doc) sp.set('type_doc', params.type_doc);
  if (params.date_from) sp.set('date_from', params.date_from);
  if (params.date_to) sp.set('date_to', params.date_to);
  if (params.limit) sp.set('limit', String(params.limit));
  if (params.offset) sp.set('offset', String(params.offset));
  const res = await fetch(`${API}?${sp}`);
  if (!res.ok) throw new Error('listContracts failed');
  return res.json();
}

export async function searchContracts(
  q: string,
  opts: { status?: string; type_doc?: string; limit?: number; offset?: number; date_from?: string; date_to?: string } = {},
): Promise<ContractSearchHit[]> {
  const sp = new URLSearchParams({ q });
  if (opts.status) sp.set('status', opts.status);
  if (opts.type_doc) sp.set('type_doc', opts.type_doc);
  if (opts.limit) sp.set('limit', String(opts.limit));
  if (opts.offset) sp.set('offset', String(opts.offset));
  if (opts.date_from) sp.set('date_from', opts.date_from);
  if (opts.date_to) sp.set('date_to', opts.date_to);
  const res = await fetch(`${API}/search?${sp}`);
  if (!res.ok) throw new Error('searchContracts failed');
  return res.json();
}

export async function getContract(id: string): Promise<ContractDetail> {
  const res = await fetch(`${API}/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error('getContract failed');
  return res.json();
}

export async function getContractTypes(): Promise<string[]> {
  const res = await fetch(`${API}/types`);
  if (!res.ok) throw new Error('getContractTypes failed');
  return res.json();
}

export async function getContractDocxBlob(id: string): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`${API}/${encodeURIComponent(id)}/docx`, { method: 'POST' });
  if (!res.ok) throw new Error('DOCX generation failed');
  const blob = await res.blob();
  const disp = res.headers.get('Content-Disposition') || '';
  const m = disp.match(/filename\*=UTF-8''(.+)/);
  const filename = m ? decodeURIComponent(m[1]) : `contract-${id}.docx`;
  return { blob, filename };
}

export async function downloadContractDocx(id: string): Promise<void> {
  const { blob, filename } = await getContractDocxBlob(id);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.download = filename;
  a.href = url;
  a.click();
  URL.revokeObjectURL(url);
}
